import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LegalFooter } from "@/components/Brand";
import { RoleTabs } from "@/components/RolePicker";
import { LoginForm } from "@/components/LoginForm";
import { Ion } from "@/components/Ion";
import { ShoppingBag } from "lucide-react";
import { ROLE_TABS, accentFor, homeRouteFor } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";

const LOGO = "/alr-ops-logo.png";
const PORTAL_KEYS = ["franchisee", "franchisee_employee", "machine_owner", "rental_customer"];
const PORTALS = PORTAL_KEYS.map((k) => ROLE_TABS.find((t) => t.key === k));

const TopBar = ({ solid, onLogin }) => (
  <nav className={`fixed inset-x-0 top-0 z-30 transition-colors duration-300 ${solid ? "border-b border-white/10 bg-[#0A0A0A]" : "bg-transparent"}`} data-testid="landing-topbar">
    <div className="mx-auto flex h-[72px] max-w-[1280px] items-center justify-between px-6 lg:px-10">
      <a href="/" className="flex items-center" data-testid="landing-logo"><img src={LOGO} alt="ALR Ops" className="h-auto w-[130px] sm:w-[210px]" /></a>
      <div className="flex items-center gap-2 sm:gap-3">
        <a href="/shop" data-testid="topbar-shop-btn" className="flex items-center gap-2 rounded-lg border border-[#4CCC06] px-3 py-2.5 text-xs font-extrabold tracking-[2px] text-white transition-colors duration-150 hover:bg-[#4CCC06] hover:text-black sm:px-5">
          <ShoppingBag size={15} /><span className="hidden sm:inline">SHOP SAFETY GEAR</span><span className="sm:hidden">SHOP</span>
        </a>
        <button onClick={onLogin} data-testid="topbar-login-btn" className="flex items-center gap-2 rounded-lg bg-[#4CCC06] px-5 py-2.5 text-xs font-extrabold tracking-[2px] text-black transition-opacity duration-150 hover:opacity-90">
          <Ion name="log-in-outline" size={16} color="#000" />LOGIN
        </button>
      </div>
    </div>
  </nav>
);

const PortalCard = ({ tab, i, onPick }) => (
  <button onClick={() => onPick(tab.key)} data-testid={`role-picker-${tab.key}`} aria-label={`${tab.label}: ${tab.subtitle} Log in`}
    className="landing-rise group relative block aspect-[8/3] w-full overflow-hidden rounded-lg border border-white/10 bg-[#0A0A0A] text-left shadow-[0_8px_24px_rgba(0,0,0,0.45)] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-[#4CCC06]/70 hover:shadow-[0_14px_40px_rgba(76,204,6,0.28)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4CCC06]"
    style={{ animationDelay: `${450 + i * 90}ms` }}>
    <img src={`/portal-${tab.key}.webp`} alt="" draggable="false" className="h-full w-full object-cover transition-[filter] duration-300 group-hover:brightness-110" data-testid={`portal-banner-${tab.key}`} />
    <span className="sr-only">{tab.label}. {tab.subtitle}</span>
  </button>
);

const LoginPanel = ({ role, setRole, onClose }) => {
  const accent = accentFor(role);
  const meta = ROLE_TABS.find((x) => x.key === role);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="login-panel">
      <button aria-label="Close login" onClick={onClose} className="landing-fade absolute inset-0 bg-black/70" data-testid="login-panel-backdrop" />
      <aside className="landing-slide relative flex h-full w-full max-w-[480px] flex-col overflow-y-auto border-l border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
        <div className="mb-8 flex items-center justify-between">
          <img src={LOGO} alt="ALR Ops" className="h-auto w-[150px]" />
          <button onClick={onClose} data-testid="role-picker-back" aria-label="Close" className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-[#333] bg-[#1A1A1A]"><Ion name="close" size={20} color="#fff" /></button>
        </div>
        <p className="text-[11px] font-bold tracking-[3px]" style={{ color: accent }} data-testid="login-eyebrow">{meta.eyebrow}</p>
        <h2 className="mt-1 text-[28px] font-extrabold leading-8">Sign in</h2>
        <p className="mb-6 mt-1 text-[13px] leading-[18px] text-[#A0A0A0]" data-testid="login-subtitle">{meta.subtitle}</p>
        <div className="space-y-4">
          <RoleTabs active={role} onPick={setRole} keys={PORTAL_KEYS} />
          <LoginForm key={role} role={role} accent={accent} />
        </div>
      </aside>
    </div>
  );
};

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const wanted = PORTAL_KEYS.includes(params.get("tab")) ? params.get("tab") : null;
  const [role, setRole] = useState(wanted || "franchisee");
  const [open, setOpen] = useState(!!(wanted || params.get("login")));
  const [solid, setSolid] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      const next = sessionStorage.getItem("alr_after_login");
      sessionStorage.removeItem("alr_after_login");
      navigate(next || homeRouteFor(user), { replace: true });
    }
  }, [loading, user, navigate]);
  useEffect(() => {
    const on = () => setSolid(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const pick = (key) => { setRole(key); setOpen(true); };
  if (loading || user) return <div className="min-h-screen bg-[#0A0A0A]" data-testid="login-loading" />;

  return (
    <div className="relative min-h-screen bg-[#0A0A0A] font-body text-white" data-testid="login-page">
      <TopBar solid={solid || open} onLogin={() => pick("franchisee")} />
      <section className="relative flex min-h-screen items-end overflow-hidden pb-14 pt-[120px] lg:items-center lg:pb-20" data-testid="landing-hero">
        <video className="absolute inset-0 h-full w-full object-cover" poster="/hero-poster.jpg" autoPlay muted loop playsInline preload="auto" aria-hidden="true" data-testid="hero-video">
          <source src="/hero.webm" type="video/webm" />
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/40" />
        <div className="relative mx-auto w-full max-w-[1280px] px-6 lg:px-10">
          <p className="landing-rise text-[11px] font-bold tracking-[4px] text-[#4CCC06]" style={{ animationDelay: "60ms" }} data-testid="hero-eyebrow">ALR MEMBER PORTAL</p>
          <h1 className="mt-4 text-5xl font-black leading-[0.95] tracking-[-1px] sm:text-6xl lg:text-[88px]" data-testid="hero-headline">
            <span className="landing-rise block" style={{ animationDelay: "120ms" }}>CLEANER.</span>
            <span className="landing-rise block" style={{ animationDelay: "200ms" }}>FASTER.</span>
            <span className="landing-rise block text-[#4CCC06]" style={{ animationDelay: "280ms" }}>SMARTER.</span>
          </h1>
          <p className="landing-rise mt-6 max-w-[560px] text-base leading-6 text-[#D0D0D0] sm:text-lg sm:leading-7" style={{ animationDelay: "360ms" }} data-testid="hero-subtitle">
            Your training, tools, AI agents and support for Advanced Laser Restoration, in one place. Choose your portal to sign in.
          </p>
          <div className="mt-10 grid max-w-[1180px] grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="role-picker">
            {PORTALS.map((tab, i) => <PortalCard key={tab.key} tab={tab} i={i} onPick={pick} />)}
          </div>
          <a href="/shop" className="landing-rise group mt-6 inline-flex items-center gap-3 rounded-full border border-[#4CCC06]/60 bg-black/40 px-5 py-3 text-sm font-bold tracking-[1px] text-white backdrop-blur-md transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#4CCC06] hover:text-black" style={{ animationDelay: "520ms" }} data-testid="landing-shop-button">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#4CCC06] text-black transition-colors group-hover:bg-black group-hover:text-[#4CCC06]"><ShoppingBag size={15} /></span>
            SHOP SAFETY GEAR
            <span className="text-xs font-semibold tracking-normal text-[#A0A0A0] group-hover:text-black/70">Laser glasses, screens and respirators. No login needed.</span>
          </a>
        </div>
      </section>
      <div className="mx-auto max-w-[1280px] px-6 pb-6 lg:px-10"><LegalFooter /></div>
      {open && <LoginPanel role={role} setRole={setRole} onClose={() => setOpen(false)} />}
    </div>
  );
}
